import styled from '@emotion/styled';
import React, { forwardRef } from 'react';

import { getColorValue, getTextSize, u } from '../../utilities';

import Align from '../align/align';
import Container from '../container/container';
import Icon from '../icon/icon';

type SizesStylesProps = {
  [key: string]: {
    height: string;
    width: string;
    iconSize: number;
    padding: string | number;
    withIconPadding: string | number;
    passwordAdditionalPadding: string | number;
    textSize: string;
    radius: string;
  };
};

const sizesStyles: SizesStylesProps = {
  small: {
    height: u(4),
    width: u(5),
    iconSize: 2,
    padding: u(1.75),
    withIconPadding: u(4.5),
    passwordAdditionalPadding: u(0.5),
    textSize: 'small',
    radius: 'var(--lns-radius-150)',
  },
  medium: {
    height: 'var(--lns-formFieldHeight)',
    width: u(6),
    iconSize: 3,
    padding: 'var(--lns-formFieldHorizontalPadding)',
    withIconPadding: u(5.5),
    passwordAdditionalPadding: u(0.5),
    textSize: 'medium',
    radius: 'var(--lns-radius-175)',
  },
  large: {
    height: u(7),
    width: u(6),
    iconSize: 3,
    padding: 'var(--lns-formFieldHorizontalPadding)',
    withIconPadding: u(5.5),
    passwordAdditionalPadding: u(0.5),
    textSize: 'large',
    radius: 'var(--lns-radius-250)',
  },
};

const inputRightPadding = (
  props: InputFieldProps & Pick<TextInputProps, 'type'>
): string | number => {
  let padding = props.addOn
    ? sizesStyles[props.inputSize].withIconPadding
    : sizesStyles[props.inputSize].padding;

  // Allow additional space for safari & other password filling icons to not overlap with addon
  if (props.type === 'password') {
    padding = `calc(${
      sizesStyles[props.inputSize].passwordAdditionalPadding
    } + ${padding})`;
  }

  return padding;
};

const InputField = styled.input<InputFieldProps & Pick<TextInputProps, 'type'>>`
  -webkit-appearance: none;
  font-family: inherit;
  width: 100%;
  height: ${props => sizesStyles[props.inputSize].height};
  border: none;
  color: inherit;
  background-color: ${getColorValue('formFieldBackground')};
  transition: 0.3s box-shadow;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: ${props =>
    props.icon
      ? sizesStyles[props.inputSize].withIconPadding
      : sizesStyles[props.inputSize].padding};
  padding-right: ${props => inputRightPadding(props)};
  border-radius: ${props => sizesStyles[props.inputSize].radius};
  box-shadow: inset 0 0 0
    ${props =>
      props.hasError
        ? 'var(--lns-formFieldBorderWidthFocus) var(--lns-color-danger)'
        : 'var(--lns-formFieldBorderWidth) var(--lns-color-formFieldBorder)'};

  ${props => getTextSize(sizesStyles[props.inputSize].textSize)};

  &:hover {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidthFocus)
      var(--lns-color-blurple);
  }

  &:focus {
    outline: 1px solid transparent;
    box-shadow: var(--lns-formFieldBorderShadowFocus);
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    background-color: ${getColorValue('disabledBackground')};
  }

  &:disabled:hover {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidth)
      var(--lns-color-formFieldBorder);
  }

  &::placeholder {
    color: ${getColorValue('bodyDimmed')};
  }
`;

const InputFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconSection = styled.div<IconSectionProps>`
  position: absolute;
  pointer-events: none;
  width: ${props => sizesStyles[props.size].width};
  // Width isn't equal to iconPadding because we want more space on the left than the right
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img<Pick<TextInputProps, 'isDisabled'>>`
  height: 100%;
  width: auto;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
`;

const AddOnWrapper = styled.div<IconSectionProps>`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  width: ${props => sizesStyles[props.size].width};
  top: 50%;
  transform: translateY(-50%);
`;

const TextInput = forwardRef(
  (
    {
      placeholder,
      onFocus,
      onChange,
      onBlur,
      onKeyDown,
      isDisabled,
      icon,
      type = 'text',
      value,
      hasError,
      size = 'medium',
      addOn,
      ...props
    }: TextInputProps & Omit<React.ComponentProps<typeof InputField>, 'size'>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const inputField = (
      <InputField
        type={type}
        placeholder={placeholder}
        onFocus={onFocus}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
        icon={icon}
        ref={ref}
        value={value}
        hasError={hasError}
        inputSize={size}
        addOn={addOn}
        {...props}
      />
    );

    return icon || addOn ? (
      <InputFieldWrapper>
        {icon && (
          <IconSection size={size}>
            {typeof icon === 'string' ? (
              <Container
                radius="50"
                width={sizesStyles[size].iconSize}
                height={sizesStyles[size].iconSize}
                overflow="hidden"
              >
                <Align alignment="center">
                  <Image src={icon} alt="" isDisabled={isDisabled} />
                </Align>
              </Container>
            ) : (
              <Icon
                icon={icon}
                size={sizesStyles[size].iconSize}
                color={getColorValue(isDisabled ? 'disabledContent' : 'body')}
              />
            )}
          </IconSection>
        )}
        {inputField}
        {addOn && <AddOnWrapper size={size}>{addOn}</AddOnWrapper>}
      </InputFieldWrapper>
    ) : (
      inputField
    );
  }
);

type TextInputProps = {
  placeholder?: string;
  value?: string | number;
  onFocus?: React.ReactEventHandler;
  onChange?: React.ReactEventHandler;
  onBlur?: React.ReactEventHandler;
  onKeyDown?: React.ReactEventHandler;
  isDisabled?: boolean;
  icon?: React.ReactNode | string;
  type?:
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  id?: string;
  hasError?: boolean;
  size?: 'small' | 'medium' | 'large';
  addOn?: React.ReactNode;
};

type IconSectionProps = {
  size?: 'small' | 'medium' | 'large';
};
type InputFieldProps = {
  icon?: React.ReactNode;
  hasError?: boolean;
  inputSize?: 'small' | 'medium' | 'large';
  addOn?: React.ReactNode;
};

export default TextInput;
